/**
 * The surface's daily wheel, if it has one.
 *
 * WHEEL_SPIN is the one gamification type with NO placement — `dropSlots`
 * lists it in `UNPLACED_GAMIFICATION_TYPES` because a daily spin is a
 * notification rather than a card, so it never occupies a slot and nothing in
 * the structure renders it. That left surfaces whose nav offered a daily spin
 * with no way to take one.
 *
 * This resolves the model behind the wheel section so the surface can mount
 * the prompt. It reads the SAME query key as the sublabel hook, so the two
 * share one fetch rather than polling the same model twice.
 */
import {useQueries} from "@tanstack/react-query";
import {useSelector} from "react-redux";
import {IStore} from "@/redux/defaultStore";
import getConfig from "@/utils/getConfig";
import {GamificationApi, GamificationWheelSpinConfig, GamificationWheelSpinState} from "@devour/client";
import {GAMIFICATION_MODEL_QUERY_KEY} from "./useGetGamificationModel";
import type {SublabelSection} from "./useGetSectionSublabels";

export interface SurfaceWheelModel {
    modelId: string;
    spinWheelConfigId?: string;
    /** Zero for a signed-out viewer, or a member who has used today's spin. */
    spinsRemaining: number;
}

export function useGetSurfaceWheel(sections: ReadonlyArray<SublabelSection> | undefined): SurfaceWheelModel | undefined {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    const gamificationSections = (sections ?? [])
        .filter(s => s.engagementType === "GAMIFICATION" && s.engagementId);

    const results = useQueries({
        queries: gamificationSections.map(s => ({
            queryKey: [GAMIFICATION_MODEL_QUERY_KEY, s.engagementId, Boolean(fullToken?.token)],
            queryFn: () => new GamificationApi(getConfig(fullToken)).getGamificationModelsId({id: s.engagementId!}),
            refetchOnWindowFocus: true,
            refetchInterval: 30000,
        })),
    });

    const model = results.map(r => r.data).find(m => m?.type === "WHEEL_SPIN");
    if (!model) return undefined;

    const config: Partial<GamificationWheelSpinConfig> | undefined = model.config;
    const state: Partial<GamificationWheelSpinState> | undefined = model.participation?.userState;

    /*
     * No participation record means the member has never spun — a FULL
     * allowance, not zero. Zero-by-default hid the prompt from exactly the
     * member it exists for. Signed out stays zero: there is no spin to offer.
     */
    const firstTimeAllowance = fullToken?.token ? config?.spinsPerPeriod ?? 1 : 0;

    return {
        modelId: model.id,
        spinWheelConfigId: config?.spinWheelConfigId,
        spinsRemaining: state?.spinsRemaining ?? firstTimeAllowance,
    };
}
